// frontend/src/modules/comment-monitor/PostsMonitorPage.tsx
import React, { useEffect, useState } from 'react';
import { useCommentMonitor } from './useCommentMonitor.ts';
import { PostCard } from './PostCard.tsx';
import { CommentsModal } from './CommentsModal.tsx';
import { useAssets } from '../ad-assets/useAssets.ts';

export function PostsMonitorPage() {
  const {
    posts,
    loading,
    error,
    fetchPosts,
    syncAll,
    syncOne,
    openedPost,
    comments,
    commentsLoading,
    openPost,
    closePost,
    hideComment,
    hideMultipleComments,
    isHidingMultiple,
    readComment,
    toggleMonitor,
    autoRefreshMinutes,
    setAutoRefreshMinutes,
    countdownSeconds,
    filterGroupId,
    setFilterGroupId,
    sortBy,
    setSortBy,
    syncProgress,
  } = useCommentMonitor();

  const { groups, fetchGroups } = useAssets();

  const [timerDraft, setTimerDraft] = useState<string>(autoRefreshMinutes ? autoRefreshMinutes.toString() : '');
  const [monitorFilter, setMonitorFilter] = useState<'all' | 'monitored' | 'unmonitored'>('all');

  const formatCountdown = (secs: number) => {
    const m = Math.floor(secs / 60).toString().padStart(2, '0');
    const s = (secs % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  const handleSaveTimer = () => {
    const val = Number(timerDraft);
    if (val > 0) {
      setAutoRefreshMinutes(val);
    } else {
      setAutoRefreshMinutes(null);
      setTimerDraft('');
    }
  };

  // Завантажуємо список при відкритті сторінки
  useEffect(() => {
    fetchPosts();
    fetchGroups();
  }, [fetchPosts, fetchGroups]);

  return (
    <div className="page-body">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 'var(--space-5)' }}>
        <div>
          <h1 style={{ fontSize: '1.5rem', marginBottom: 'var(--space-1)' }}>
            Моніторинг коментарів
          </h1>
          <p className="text-muted text-sm">
            Відстежуйте та приховуйте коментарі під вашими постами
          </p>
        </div>
        
        <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <label htmlFor="refresh-interval" className="text-sm text-subtle">Оновлювати кожні (хв):</label>
            <input 
              id="refresh-interval"
              type="number" 
              className="filter-input"
              style={{ width: '80px' }}
              min="1"
              value={timerDraft}
              onChange={(e) => setTimerDraft(e.target.value)}
              placeholder="Вимк."
            />
            <button className="btn btn--secondary" onClick={handleSaveTimer} style={{ padding: '4px 8px', fontSize: '0.75rem' }}>
              Зберегти
            </button>
            {countdownSeconds !== null && (
              <span className="text-sm" style={{ marginLeft: '8px', fontWeight: 'bold', color: 'var(--color-primary)' }}>
                {formatCountdown(countdownSeconds)}
              </span>
            )}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <select 
              className="filter-input"
              value={filterGroupId ?? ''}
              onChange={e => setFilterGroupId(e.target.value ? parseInt(e.target.value, 10) : null)}
            >
              <option value="">Всі групи</option>
              {groups.map(g => (
                <option key={g.id} value={g.id}>{g.name}</option>
              ))}
            </select>

            <select 
              className="filter-input"
              value={monitorFilter}
              onChange={e => setMonitorFilter(e.target.value as any)}
            >
              <option value="all">Всі пости</option>
              <option value="monitored">Моніторяться (On)</option>
              <option value="unmonitored">Не моніторяться (Off)</option>
            </select>

            <select 
              className="filter-input"
              value={sortBy}
              onChange={e => setSortBy(e.target.value)}
            >
              <option value="unread_comments_desc">Нові коментарі</option>
              <option value="total_comments_desc">Усі коментарі</option>
              <option value="created_time_desc">Нові пости</option>
            </select>
          </div>

          <button 
            className="btn btn--primary" 
            onClick={() => syncAll()}
            disabled={loading}
          >
            {loading 
              ? (syncProgress ? `Оновлюється (${syncProgress.current}/${syncProgress.total})` : 'Оновлюється...') 
              : 'Оновити зараз'}
          </button>
        </div>
      </div>

      {error && (
        <div className="notification notification--error" style={{ marginBottom: 'var(--space-4)' }}>
          {error}
        </div>
      )}

      {loading && posts.length === 0 ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 'var(--space-4)' }}>
          {[1, 2, 3].map(i => (
            <div key={i} className="skeleton" style={{ height: 180, borderRadius: 'var(--radius-md)' }} />
          ))}
        </div>
      ) : posts.length === 0 ? (
        <div className="glass-card empty-state">
          <div className="empty-state__title">Немає постів для моніторингу</div>
          <div className="empty-state__desc">
            Спочатку підключіть сторінки та синхронізуйте їх у розділі "Пости".
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
          {posts
            .filter(post => {
              if (monitorFilter === 'monitored') return post.isMonitored;
              if (monitorFilter === 'unmonitored') return !post.isMonitored;
              return true;
            })
            .map(post => (
            <PostCard 
              key={post.id} 
              post={post} 
              onOpen={openPost} 
              onSync={syncOne} 
              onToggleMonitor={toggleMonitor}
            />
          ))}
        </div>
      )}

      {openedPost && (
        <CommentsModal 
          comments={comments}
          loading={commentsLoading}
          onClose={closePost}
          onHide={hideComment}
          onHideMultiple={hideMultipleComments}
          isHidingMultiple={isHidingMultiple}
          onRead={readComment}
        />
      )}
    </div>
  );
}
